import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useTheme } from "@mui/material/styles";


import MenuIcon from "@mui/icons-material/Menu";
import logo from "assets/images/logo_icon.png";

interface HeaderProps {
    brand: string;
    links: string[];
    context: "landing" | "external";
}


const Header: React.FC<HeaderProps> = ({ brand, links, context }) => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();

    const theme = useTheme();
    const drawerBg = scrolled ? "#fff" : "transparent";
    const drawerColor = scrolled ? "black" : "white";
    const drawerFilter = !scrolled ? "saturate(180%) blur(10px)" : "none";


    const handleClick = (link: string) => {
        const lower = link.toLowerCase();

        if (lower === "login") {
            navigate("/auth");
            return;
        }
        if (context === "landing") {
            const el = document.getElementById(lower);
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            window.location.href = `/LandingPage.html#${lower}`;
        }
    };

    const toggleMobile = () => {
        setMobileOpen((open) => !open);
    };

    useEffect(() => {
        const heroHeight = window.innerHeight * 0.85;
        const onScroll = () => setScrolled(window.scrollY > heroHeight);

        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <AppBar
                position="fixed"
                elevation={scrolled ? 4 : 0}
                sx={{
                    backgroundColor: scrolled ? "#fff" : "transparent",
                    color: scrolled ? "black" : "white",
                    transition: "all 300ms ease",
                    boxShadow: scrolled ? 3 : "none",
                    backdropFilter: !scrolled ? "saturate(180%) blur(10px)" : "none",
                }}
            >
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                        <Box component="img" src={logo} alt="Logo" sx={{ height: 20, mr: 1 }} />
                        {brand}
                    </Typography>

                    {/* desktop links */}
                    <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
                        {links.map((link) => (
                            <Button key={link} color="inherit" onClick={() => handleClick(link)}>
                                {link}
                            </Button>
                        ))}
                    </Box>

                    {/* mobile menu button */}
                    <Box sx={{ display: { xs: "block", md: "none" } }}>
                        <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleMobile}>
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* mobile drawer */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={toggleMobile}
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: {
                        height: "auto",
                        width: "auto",        // shrink to content
                        minWidth: 160,        // don’t go smaller than this
                        bgcolor: drawerBg,    // same as AppBar
                        color: drawerColor,   // match text color
                        backdropFilter: drawerFilter,  // same blur as AppBar
                    },
                }}
            >
                <Box
                    role="presentation"
                    onClick={toggleMobile}
                    onKeyDown={toggleMobile}
                    sx={{ width: 250, p: 1 }}
                >
                    <List disablePadding>
                        {links.map((link) => (
                            <ListItem key={link} disablePadding>
                                <ListItemButton onClick={() => handleClick(link)}>
                                    <ListItemText primary={link} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>
        </>
    );
};

export default Header;
